// review-decision — ONE shape for a staged review decision, and ONE rule for
// what blocks it.
//
// Pure. No React, no fetch, no DOM. Every function here is a value in and a
// value out, so the rules are tested without a browser
// (`review-decision.test.ts`).
//
// WHY IT EXISTS. Five call sites used to assemble the decision object by hand,
// each in a slightly different shape, and each repeating the same two rules:
// drop an empty reason, and carry the reviewer's staged answers on an approve
// only. A rule written five times is a rule that holds four times: the
// monthly-narrative site emitted snake_case keys into an untyped bag, and
// every approval discarded the chosen variant in production without a word.
//
// `buildDecision` is now the only way a decision is written, and its payload
// is the payload of THAT kind. `ReviewPayload<K>` comes from the wire union in
// `@septima/contract`, so a field aimed at the wrong kind is a compile error.
//
// `blockReason` is the other half. SEP-201 says an unreadable notice date must
// never reach a live corporate action. The rule has two enforcement points:
// this file, which greys the Approve button and says why, and the server
// precheck, which refuses the approval before any write. This file holds the
// web half exactly once, so the button and the message can no longer disagree.

import type {
    CoacNoticeDate,
    ReviewDecision,
    ReviewDecisionVerb,
    ReviewPayload,
    ReviewableKind,
} from '@septima/contract';

/** The reviewer's staged answer for one artefact, before it is submitted. */
export interface StagedDecision<K extends ReviewableKind> {
    readonly kind: K;
    readonly id: string;
    readonly decision: ReviewDecisionVerb;
    /** Free text. Blank and absent mean the same thing. */
    readonly reason?: string;
    /** The typed answers the kind asks for. A kind that asks for none cannot
     *  be given one: the type of this field is `never` for those kinds. */
    readonly payload?: ReviewPayload<K>;
}

/**
 * Turn one staged answer into the wire decision.
 *
 * Two rules, held once:
 *
 *   1. A blank reason is no reason. It is dropped, so the server applies its
 *      own default rather than storing an empty string.
 *   2. THE PAYLOAD RIDES AN APPROVE ONLY. A reject or a request-revision
 *      carries no assignment, no promotion, no correction and no selection:
 *      the reviewer said no, so their staged answers are not facts.
 */
export function buildDecision<K extends ReviewableKind>(staged: StagedDecision<K>): ReviewDecision {
    const reason = staged.reason?.trim();
    const base = {
        target: 'artefact',
        kind: staged.kind,
        id: staged.id,
        decision: staged.decision,
        ...(reason ? { reason } : {}),
    };
    if (staged.decision !== 'approve' || staged.payload === undefined) {
        return base as ReviewDecision;
    }
    return { ...base, payload: staged.payload } as ReviewDecision;
}

// ---------------------------------------------------------------------------
// SEP-201 — the notice-date block
// ---------------------------------------------------------------------------

/**
 * One notice date as the reviewer currently has it: the reading the server
 * sent, and the text now in the box.
 */
export interface StagedNoticeDate {
    readonly notice: CoacNoticeDate;
    readonly value: string;
}

/**
 * Is the reading of the server still about the text in the box?
 *
 * The reading describes `notice.raw` and nothing else. As soon as the reviewer
 * types, the reading describes a value nobody can see, and the next fetch
 * gives the new one. Until then this module shows no reading and blocks no
 * approval: the approval Action does the last check on the server.
 */
export function readingIsCurrent(notice: CoacNoticeDate, value: string): boolean {
    return value === (notice.raw ?? '');
}

/**
 * Why the corporate-action approval is blocked, or null when it can go.
 *
 * A date that the server could not read blocks the approval, because an
 * unreadable regulated date must never reach a live row. The block lifts as
 * soon as the reviewer edits that box, for the reason above.
 */
export function blockReason(dates: readonly StagedNoticeDate[]): string | null {
    const unreadable = dates.some(
        ({ notice, value }) => notice.problem !== null && readingIsCurrent(notice, value),
    );
    // The copy is a product decision and is unchanged from the panel it came
    // from. UI copy is out of scope for a refactor (CLAUDE.md).
    return unreadable ? 'Correct the dates above before you approve.' : null;
}
